import { VendorType } from 'new/components/settings/VendorBuilder/VendorBuilderContext';
import { EndpointType } from 'new/components/settings/VendorBuilder/types';
import { has, includes, isEmpty, split, isNumber } from 'lodash';

const OAUTH2_RESPONSE_MACROS = [
  'oauth2.expires_in',
  'oauth2.token_type',
  'oauth2.refresh_token',
  'oauth2.authorizedAt',
  'oauth2.access_token',
];
const OAUTH1_RESPONSE_MACROS = ['oauth1a.access_token', 'oauth1a.oauth_token_secret'];

function convertSpecToVendor(spec: any): VendorType {
  const vendorName = Object.keys(spec)[0];
  const vendor = spec[vendorName]['POST /vendors'] as VendorType;
  const endpointsMap = spec[vendorName]['POST /vendor_endpoints'] as EndpointType[];
  const allResourceParameters = spec[vendorName]['POST /resource_parameters'] as {
    [key: string]: EndpointType['parameters'][number][];
  };
  Object.keys(allResourceParameters).forEach((paramEndpointFor) => {
    const endpointName = paramEndpointFor.replace('for: ', '');
    if (endpointsMap[endpointName]) {
      if (!endpointsMap[endpointName].parameters) {
        endpointsMap[endpointName].parameters = [];
      }
      endpointsMap[endpointName].parameters.push(...allResourceParameters[paramEndpointFor]);
    }
  });
  vendor.endpoints = Object.values(endpointsMap);
  return vendor;
}

export default function validateVendor(spec: any): { [key: string]: string } {
  const errors = {};
  const vendor = convertSpecToVendor(spec);
  if (!vendor.name) {
    errors['name'] = 'Is required';
  }
  if (!vendor.display_name) {
    errors['display_name'] = 'Is required';
  }
  validateTemplates(vendor, errors);
  validateEndpoints(vendor, errors);
  return errors;
}

const regexParam = /\$\{(.*?)}/g;
const regexCredParam = /\{data_credential\["(.*?)"]}/g;

function validateAuthTemplateMacros(template, index, mapParams, errors: any, oAuthType = 'none') {
  Object.keys(template.config).forEach((configKey: string) => {
    const configValue = template.config[configKey];
    if (typeof configValue !== 'string') return;

    if (includes(configValue, '{') && includes(configValue, '}') && !includes(configValue, '${')) {
      errors[`auth_templates[${index}].config["${configKey}"]`] =
        `Potential incorrect macro usage. Macros should begin with \${. : ${configValue}`;
    }

    const matches = configValue.matchAll(regexParam);
    for (let mat of matches) {
      const paramName = mat[1];
      if (!mapParams[paramName]) {
        //exclude for oauth1 & oauth2 3-legged params that are autogenerated by UI
        if (
          (oAuthType === '3-legged-OAUTH2' && includes(OAUTH2_RESPONSE_MACROS, paramName)) ||
          (oAuthType === '3-legged-OAUTH1' && includes(OAUTH1_RESPONSE_MACROS, paramName))
        ) {
          //move to next validation rule.

          continue;
        }

        errors[`auth_templates[${index}].config["${configKey}"] - ${paramName}`] =
          'Using a parameter name that is not defined';
      }
    }
  });
}

function validateEndpointMacros(
  endpoint: EndpointType,
  endpointIndex: number,
  mapAuthConfigKeys: { [key: string]: any }[],
  mapEndpointParams: { [key: string]: any },
  errors: any,
) {
  const isSource = !!endpoint.source_template;
  if (isSource) {
    endpoint.source_template['rest.iterations'].forEach((iteration, iterationIndex) => {
      Object.keys(iteration).forEach((key, index) => {
        const value = iteration[key];
        if (
          (key === 'method' && isEmpty(value)) ||
          (key === 'response.data.path' && isEmpty(value))
        ) {
          errors[
            `endpoints[${endpointIndex}].source_template["rest.iterations"][${iterationIndex}] - ${key}`
          ] = `${key} cannot be ${value}`;
        }

        if (typeof value !== 'string') return;

        if (
          includes(value, 'rest.iterations') &&
          split(value, 'rest.iterations').length !== split(value, '${rest.iterations').length
        ) {
          errors[
            `endpoints[${endpointIndex}].source_template["rest.iterations"][${iterationIndex}] - ${key}`
          ] = 'Macro rest.iterations must begin with ${';
        }

        if (
          includes(value, 'data_credential') &&
          split(value, 'data_credential').length === split(value, '${data_credential').length
        ) {
          errors[
            `endpoints[${endpointIndex}].source_template["rest.iterations"][${iterationIndex}] - ${key}`
          ] = 'Macro data_credential cannot begin with ${. It should be {';
        }

        const matches = value.matchAll(regexParam);
        const credMatches = value.matchAll(regexCredParam);
        for (let mat of matches) {
          const paramName = mat[1];
          if (paramName.startsWith('rest.iterations')) {
            if (!mapEndpointParams[paramName]) {
              errors[
                `endpoints[${endpointIndex}].source_template["rest.iterations"][${iterationIndex}] - ${paramName}`
              ] = 'Using a parameter name that is not defined in resource parameters';
            }
          } else {
            if (
              !mapAuthConfigKeys.every((map) => map[paramName]) &&
              !mapEndpointParams[paramName]
            ) {
              errors[
                `endpoints[${endpointIndex}].source_template["rest.iterations"][${iterationIndex}] - ${paramName}`
              ] = 'Using a parameter name that is not defined in all auth_templates config';
            }
          }
        }
        for (let credMat of credMatches) {
          const paramName = credMat[1];
          if (!mapAuthConfigKeys.every((map) => map[paramName])) {
            errors[
              `endpoints[${endpointIndex}].source_template["rest.iterations"][${iterationIndex}] - ${paramName}`
            ] = 'Using a parameter name that is not defined in all auth_templates config';
          }
        }
      });
    });
  } else {
    const sinkTemplate = endpoint.sink_template;
    function validateSinkValue(value, propKey) {
      if (typeof value === 'object') {
        Object.keys(value).forEach((key) => {
          validateSinkValue(value[key], key);
        });
      } else {
        if (propKey === 'method' && isEmpty(value)) {
          errors[`endpoints[${endpointIndex}].sink_template - ${propKey}`] =
            `${propKey} cannot be ${value}`;
        }
        if (propKey === 'body.template' && value !== '{message.json}') {
          errors[`endpoints[${endpointIndex}].sink_template - ${propKey}`] =
            `${propKey} should usually be {message.json}. Are you sure?`;
        }

        if (typeof value !== 'string') return;

        if (
          includes(value, 'url.template') &&
          split(value, 'url.template').length !== split(value, '${url.template').length
        ) {
          errors[`endpoints[${endpointIndex}].sink_template - ${propKey}`] =
            'Macro url.template must begin with ${';
        }

        if (
          includes(value, 'data_credential') &&
          split(value, 'data_credential').length === split(value, '${data_credential').length
        ) {
          errors[`endpoints[${endpointIndex}].sink_template - ${propKey}`] =
            'Macro data_credential cannot begin with ${. It should be {';
        }

        const matches = value.matchAll(regexParam);
        const credMatches = value.matchAll(regexCredParam);
        for (let mat of matches) {
          const paramName = mat[1];
          if (paramName.startsWith('rest.iterations') || paramName.startsWith('url.template')) {
            if (!mapEndpointParams[paramName]) {
              errors[`endpoints[${endpointIndex}].sink_template - ${paramName}`] =
                'Using a parameter name that is not defined in resource parameters';
            }
          } else {
            if (
              !mapAuthConfigKeys.every((map) => map[paramName]) &&
              !mapEndpointParams[paramName]
            ) {
              errors[`endpoints[${endpointIndex}].sink_template - ${paramName}`] =
                'Using a parameter name that is not defined in auth_templates config';
            }
          }
        }
        for (let credMat of credMatches) {
          const paramName = credMat[1];
          if (!mapAuthConfigKeys.every((map) => map[paramName])) {
            errors[`endpoints[${endpointIndex}].sink_template - ${paramName}`] =
              'Using a parameter name that is not defined in all auth_templates config';
          }
        }
      }
    }

    Object.keys(sinkTemplate).forEach((key) => {
      const value = sinkTemplate[key];
      validateSinkValue(value, key);
    });
  }
}

function validateTemplates(vendor: VendorType, errors) {
  const { auth_templates: templates } = vendor;
  const templateNames = {};
  templates.forEach((template, index) => {
    const paramNames = {};
    if (templateNames[template.name]) {
      errors[`auth_templates[${index}].name`] = 'You should not use duplicate names for templates';
    }
    if (!template.name.startsWith(vendor.name)) {
      errors[`auth_templates[${index}].name`] =
        'Template names should start with vendor name + "."';
    }
    if (!template.name) {
      errors[`auth_templates[${index}].name`] = 'Is required';
    }
    if (!template.display_name) {
      errors[`auth_templates[${index}].display_name`] = 'Is required';
    }

    if (
      has(template, 'config["test.url"]') &&
      isEmpty(template.config['test.url']) &&
      template.config['skip.validation'] !== true
    ) {
      errors[`auth_templates[${index}].["test.url"`] =
        'Template test url cannot be empty if skip.validation is set to false';
    }

    template.params.forEach((param, i) => {
      if (paramNames[param.name]) {
        errors[`auth_templates[${index}].params[${i}].name`] =
          'You should not use duplicate names for template params';
      }

      if (has(param, 'order') && !isNumber(param.order)) {
        errors[`auth_templates[${index}].params[${i}].order`] =
          `order in ${param.name} must be a number `;
      }

      paramNames[param.name] = true;
    });
    templateNames[template.name] = true;
    const mapParams = template.params.reduce((map, param, index) => {
      map[param.name] = true;
      return map;
    }, {});
    let oAuthType = 'none';
    if (has(template, 'config["auth.type"]')) {
      if (template.config['auth.type'] === 'OAUTH2') {
        oAuthType = `${template.config['oauth2.token_exchange_type']}-${template.config['auth.type']}`;
      } else if (template.config['auth.type'] === 'OAUTH1') {
        oAuthType = `${template.config['oauth1.token_exchange_type']}-${template.config['auth.type']}`;
      }
    }
    validateAuthTemplateMacros(template, index, mapParams, errors, oAuthType);
  });
}

function validateEndpoints(vendor: VendorType, errors) {
  const { endpoints, auth_templates } = vendor;
  const endpointNames = {};
  endpoints.forEach((endpoint, index) => {
    const paramNames = {};
    if (endpointNames[endpoint.name]) {
      errors[`endpoints[${index}].name`] = 'You should not use duplicate names for endpoints';
    }
    if (!endpoint.name.startsWith(vendor.name)) {
      errors[`endpoints[${index}].name`] = 'Endpoint names should start with vendor name + "."';
    }
    if (!endpoint.name) {
      errors[`endpoints[${index}].name`] = 'Is required';
    }
    if (!endpoint.display_name) {
      errors[`endpoints[${index}].display_name`] = 'Is required';
    }
    endpoint.parameters.forEach((param, index) => {
      if (paramNames[param.name]) {
        errors[`endpoint[${index}].parameters[${index}].name`] =
          'You should not use duplicate names for endpoint params';
      }

      if (param.vendor_endpoint_name !== endpoint.name) {
        errors[`endpoint[${index}].parameters[${index}].name`] =
          `vendor_endpoint_name ${param.vendor_endpoint_name} does not match the expected endpoint name ${endpoint.name} for endpoint[${endpoint.name}] param[${param.name}]`;
      }

      if (!!endpoint.source_template && !param.name.startsWith('rest.iterations')) {
        errors[`endpoint[${index}].parameters[${index}].name`] =
          'Source endpoint parameters names should start with rest.iterations';
      }

      if (!!endpoint.source_template && param.resource_type !== 'SOURCE') {
        errors[`endpoint[${index}].parameters[${index}].name`] =
          'Source endpoints parameters must have resource_type = SOURCE';
      }

      if (has(param, 'order') && !isNumber(param.order)) {
        errors[`endpoint[${index}].parameters[${index}].order`] =
          `order in ${param.name} must be a number `;
      }

      if (
        !!endpoint.sink_template &&
        !param.name.startsWith('url.template') &&
        !param.name.startsWith('body.transform.function')
      ) {
        errors[`endpoint[${index}].parameters[${index}].name`] =
          'Sink endpoint parameters names should start with a sink_template property name. You can always start with url.template';
      }
      if (!!endpoint.sink_template && param.resource_type !== 'SINK') {
        errors[`endpoint[${index}].parameters[${index}].name`] =
          'Sink endpoints parameters must have resource_type = SINK';
      }

      paramNames[param.name] = true;
    });
    endpointNames[endpoint.name] = true;
    const mapAuthConfigKeys = auth_templates.map((template) =>
      Object.keys(template.config).reduce((map, key, index) => {
        map[key] = true;
        return map;
      }, {}),
    );
    const mapEndpointParams = endpoint.parameters.reduce((map, param, index) => {
      map[param.name] = true;
      return map;
    }, {});
    validateEndpointMacros(endpoint, index, mapAuthConfigKeys, mapEndpointParams, errors);
  });
}
